import { Restart16 } from '@carbon/icons-react';
import { List, Tooltip, Typography } from 'antd';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FixedSizeList } from 'react-window';
import styled, { css } from 'styled-components';
import { Meeting } from 'api/meeting/types';
import { Button } from 'components/atoms/button';
import { SearchInput } from 'components/atoms/input';
import { Loading } from 'components/atoms/loading';
import { Tag } from 'components/atoms/tag';
import { FilterBar } from 'components/molecules/filter-bar';
import { useTranslate } from 'hooks/use-translation';
import { useGetAllMeetings } from 'queries/meetings';
import { AiFillMeta } from '../types';
import {
  filterMeetings,
  getDateForMeetingsCalendar,
  getTimeForMeetingsCalendar,
} from '../utils';
import { MeetingsParticipants } from './custom-avatar-group';
import { MeetingEmptyState } from './meeting-empty-state';
import { MeetingErrorState } from './meetings-error-state';
import { NoMeetingsFound } from './no-meetings-found';

const MEETING_LIST_HEIGHT = 258;
const ACTION_BAR_HEIGHT = 48;
const LIST_ITEM_HEIGHT = 54;

export const MEETING_CONTAINER_HEIGHT = MEETING_LIST_HEIGHT + ACTION_BAR_HEIGHT;

export enum MeetingCompanyFilterType {
  COMPANY = 'COMPANY',
  ALL = 'ALL',
}

const isMeetingSummaryAvailable = (meeting: Meeting) =>
  meeting.summaryAvailable;

const getTooltipConfigForMeetingName = (meeting: Meeting) => {
  return isMeetingSummaryAvailable(meeting) ? meeting.meetingName : '';
};

interface Props {
  onGenerate: (meetings: Meeting[]) => void;
  classNameForWrapper?: string;
  meta?: AiFillMeta;
}

const Wrapper = styled.div`
  width: 400px;
`;

const TagWrapper = styled.div<{ $hide?: boolean }>`
  display: ${(props) => (props.$hide ? 'none' : 'flex')};
`;

const StyledSecondaryFilterBar = styled(FilterBar.Secondary)`
  display: flex;
  gap: var(--spacing-03);

  min-width: inherit;
`;

const StyledSearchInput = styled(SearchInput)``;

export const IconWrapper = styled.span`
  color: var(--icon-primary);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledListItem = styled(List.Item)<{ $disabled?: boolean }>`
  padding: var(--spacing-03) var(--spacing-06);

  &:hover {
    background-color: var(--hover-color);
  }
  ${({ $disabled }) =>
    $disabled
      ? css`
          cursor: not-allowed;
          opacity: 50%;
        `
      : ''};
  .ant-typography-ellipsis {
    width: 100%;
  }
`;

const ItemDescriptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-02);
  color: var(--gray-60);
  font-size: var(--font-size-sm);
`;

const MeetingListWrapper = styled.div``;

const DateWrapper = styled.div``;
const Divider = styled.div``;
const TimeWrapper = styled.div``;

const StyledPointerDisable = styled.div<{ $disabled?: boolean }>`
  ${({ $disabled }) =>
    $disabled
      ? css`
          pointer-events: none;
        `
      : ''};
`;

const StyledSearchInputWrapper = styled.div``;

const StyledFilterBar = styled(FilterBar)<{ $disabled?: boolean }>`
  padding: var(--spacing-03);

  ${StyledPointerDisable} {
    ${({ $disabled }) =>
      $disabled
        ? css`
            pointer-events: none;
          `
        : ''};
  }

  ${FilterBar.Primary},${StyledSearchInputWrapper} {
    opacity: ${(props) => (props.$disabled ? '50%' : 'auto')};
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'auto')};
  }
`;

const LoadingWrapper = styled.div`
  height: ${MEETING_CONTAINER_HEIGHT}px;
  width: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

/**
 *
 * @description This component is used to select meetings for generating AI Fill. This is unrestricted since the
 * below component does on restrict based on whether the timesheet calendar is connected or not. Use `MeetingSelector`
 * component instead.
 */
export const MeetingSelectorUnrestricted = ({
  classNameForWrapper,
  onGenerate,
  meta,
}: Props) => {
  const {
    data: meetings,
    isFetching: isMeetingsLoading,
    refetch: refetchMeetings,
    isError,
  } = useGetAllMeetings();

  const [searchText, setSearchText] = useState('');
  const searchTextRef = useRef<string>('');
  const [hideCompanyFilter, setHideCompanyFilter] = useState(false);

  const translate = useTranslate();
  const [isCompanyFilterSelected, setIsSelectedCompanyFilter] =
    useState<boolean>(false);

  const filteredMeetings = useMemo(
    () =>
      meetings
        ? filterMeetings(
            meetings,
            isCompanyFilterSelected
              ? MeetingCompanyFilterType.COMPANY
              : MeetingCompanyFilterType.ALL,
            searchText,
            meta?.company !== undefined ? meta?.company?.companyId : undefined,
          )
        : [],
    [isCompanyFilterSelected, meetings, searchText, meta],
  );

  const isThereAnyCompanyMeetings = useMemo(
    () =>
      (meetings
        ? filterMeetings(
            meetings,
            MeetingCompanyFilterType.COMPANY,
            '',
            meta?.company !== undefined ? meta?.company?.companyId : undefined,
          )
        : []
      ).length !== 0,
    [meetings, meta],
  );

  const getTagColor = useCallback(
    (filterType: MeetingCompanyFilterType) => {
      if (meetings && meetings.length === 0) {
        return 'gray';
      }
      if (filterType === MeetingCompanyFilterType.COMPANY) {
        return isCompanyFilterSelected ? 'blue' : 'gray';
      }
      if (filterType === MeetingCompanyFilterType.ALL) {
        return !isCompanyFilterSelected ? 'blue' : 'gray';
      }

      return 'gray';
    },
    [meetings, isCompanyFilterSelected],
  );

  if (isMeetingsLoading || !meetings) {
    return (
      <LoadingWrapper>
        <Loading />
      </LoadingWrapper>
    );
  }

  const isActionsDisabled = () => {
    return meetings.length === 0 && searchText.length === 0;
  };

  return (
    <Wrapper className={classNameForWrapper}>
      <StyledFilterBar $disabled={isActionsDisabled()}>
        <FilterBar.Primary>
          <StyledPointerDisable>
            {meta?.company !== undefined && isThereAnyCompanyMeetings ? (
              <TagWrapper $hide={hideCompanyFilter || searchText.length !== 0}>
                <Tag
                  color={getTagColor(MeetingCompanyFilterType.COMPANY)}
                  onClick={() => {
                    setIsSelectedCompanyFilter(true);
                    setSearchText('');
                    searchTextRef.current = '';
                  }}
                >
                  <FormattedMessage
                    id="status_updates.meeting_tempalates.filter.company_meetings"
                    values={{
                      companyName: meta?.company?.companyName,
                    }}
                  />
                </Tag>
                <Tag
                  color={getTagColor(MeetingCompanyFilterType.ALL)}
                  onClick={() => setIsSelectedCompanyFilter(false)}
                >
                  <FormattedMessage id="status_updates.meeting_tempalates.filter.all_meetings" />
                </Tag>
              </TagWrapper>
            ) : null}
          </StyledPointerDisable>
        </FilterBar.Primary>

        <StyledSecondaryFilterBar>
          <StyledSearchInput
            value={searchText}
            onFocusBeforeActive={() => setHideCompanyFilter(true)}
            onChange={(e) => {
              setSearchText(e.currentTarget.value);
              searchTextRef.current = e.currentTarget.value;
              setIsSelectedCompanyFilter(false);
            }}
            onBlur={() =>
              searchTextRef.current.length ? null : setHideCompanyFilter(false)
            }
            onClose={() => setHideCompanyFilter(false)}
            size="small"
            direction="right"
            minWidth={hideCompanyFilter ? '240px' : '0px'}
            searchButtonProps={{
              type: 'text',
              disabled: isActionsDisabled(),
            }}
          />

          <Tooltip
            title={translate(
              'status_updates.meeting_tempalates.refresh_meetings.tooltip',
            )}
          >
            <Button
              type="text"
              size="small"
              onClick={() => refetchMeetings()}
              icon={
                <IconWrapper>
                  <Restart16 />
                </IconWrapper>
              }
            />
          </Tooltip>
        </StyledSecondaryFilterBar>
      </StyledFilterBar>
      <MeetingListWrapper>
        {isError ? (
          <>
            <MeetingErrorState onClick={() => refetchMeetings()} />
          </>
        ) : filteredMeetings.length === 0 ? (
          meetings.length !== 0 ? (
            <NoMeetingsFound />
          ) : (
            <MeetingEmptyState />
          )
        ) : (
          <List>
            <FixedSizeList
              itemCount={filteredMeetings?.length ?? 0}
              itemSize={LIST_ITEM_HEIGHT}
              height={MEETING_LIST_HEIGHT}
              width="100%"
            >
              {({ index, style }) => {
                return (
                  <StyledListItem
                    style={style}
                    key={filteredMeetings?.[index].meetingId}
                    onClick={() =>
                      isMeetingSummaryAvailable(filteredMeetings[index])
                        ? onGenerate([filteredMeetings[index]])
                        : null
                    }
                    $disabled={
                      !isMeetingSummaryAvailable(filteredMeetings[index])
                    }
                  >
                    <Tooltip
                      title={
                        !isMeetingSummaryAvailable(filteredMeetings[index])
                          ? translate('ai_fill.no_meetings.tooltip')
                          : ''
                      }
                      placement="bottom"
                    >
                      <List.Item.Meta
                        title={
                          <Typography.Text
                            ellipsis={{
                              tooltip: getTooltipConfigForMeetingName(
                                filteredMeetings?.[index],
                              ),
                            }}
                          >
                            {filteredMeetings?.[index].meetingName}
                          </Typography.Text>
                        }
                        description={
                          <StyledPointerDisable
                            $disabled={
                              !isMeetingSummaryAvailable(
                                filteredMeetings[index],
                              )
                            }
                          >
                            <ItemDescriptionWrapper>
                              <DateWrapper>
                                {getDateForMeetingsCalendar(
                                  filteredMeetings?.[index].startTime,
                                )}
                              </DateWrapper>
                              <Divider>·</Divider>
                              <TimeWrapper>
                                {`${getTimeForMeetingsCalendar(
                                  filteredMeetings?.[index].startTime,
                                  filteredMeetings?.[index].endTime,
                                )}`}
                              </TimeWrapper>

                              <MeetingsParticipants
                                participants={filteredMeetings?.[
                                  index
                                ].meta.participants?.slice(0, 5)}
                                maxParticipants={3}
                              />
                            </ItemDescriptionWrapper>
                          </StyledPointerDisable>
                        }
                      />
                    </Tooltip>
                  </StyledListItem>
                );
              }}
            </FixedSizeList>
          </List>
        )}
      </MeetingListWrapper>
    </Wrapper>
  );
};
